import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import Tooltip from '../Tooltip/Tooltip';
import styles from './TruncatedOptionLabel.module.scss';

interface TruncatedOptionLabelProps {
    label?: string;
    testId?: string;
    /** Re-measures on resize/hover and keeps the tooltip wrapper mounted, for layouts whose available width changes after mount. */
    remeasure?: boolean;
}

const TruncatedOptionLabel = ({ label, testId, remeasure = false }: TruncatedOptionLabelProps) => {
    const textRef = useRef<HTMLSpanElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    const measure = useCallback(() => {
        const text = textRef.current;
        if (text) setIsTruncated(text.scrollWidth > text.clientWidth);
    }, []);

    useLayoutEffect(() => {
        measure();
        if (!remeasure || typeof ResizeObserver === 'undefined' || !textRef.current) return;
        const observer = new ResizeObserver(measure);
        observer.observe(textRef.current);
        return () => observer.disconnect();
    }, [label, remeasure, measure]);

    const text = (
        <span ref={textRef} className={styles.Text} data-testid={testId} onMouseEnter={remeasure ? measure : undefined}>
            {label}
        </span>
    );

    if (!remeasure && !isTruncated) return text;

    return (
        <span className={styles.TooltipHost}>
            <Tooltip content={isTruncated ? label : ''} direction="top" fitContent usePortal containerFullWidth>
                {text}
            </Tooltip>
        </span>
    );
};

export default TruncatedOptionLabel;
